import { Timestamp } from 'firebase/firestore'
import { Roles } from '../Helper/Enums'
import { FirebaseProperties } from '../Helper/FirebaseProperties'
import { FirebaseCollections, FirestorePaths } from '@/utils/firestore/FirestorePaths'
import type {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions
} from 'firebase/firestore'
import { createFirebaseInterface } from '../Helper/FirebaseProperties'

export enum StoreAlertPriority {
  Low = 'low',
  Normal = 'normal',
  High = 'high'
}

export enum StoreAlertSource {
  Store = 'store',
  Payment = 'payment',
  Dev = 'dev'
}

export class StoreAlert extends FirebaseProperties {
  title = ''
  body = ''
  targetRoles: Roles[] = [Roles.User]
  targetAccountIds: string[] = []
  targetAll = false
  startAt: Timestamp | null = Timestamp.now()
  endAt: Timestamp | null = null
  isActive = true
  priority: StoreAlertPriority = StoreAlertPriority.Normal
  source: StoreAlertSource = StoreAlertSource.Store
  version = 1
  metadata: Record<string, string> = {}

  constructor(data?: Partial<StoreAlert>) {
    super()
    Object.assign(this, data)

    if (!Array.isArray(this.targetRoles)) this.targetRoles = []
    if (!Array.isArray(this.targetAccountIds)) this.targetAccountIds = []
    if (!this.source) this.source = StoreAlertSource.Store
    if (!this.metadata || typeof this.metadata !== 'object' || Array.isArray(this.metadata)) {
      this.metadata = {}
    }

    this.setId()
  }

  setId(storeId?: string) {
    if (!storeId) {
      this.thisCollection = ''
      this.thisDocument = ''
      return
    }

    this.thisCollection = FirestorePaths.storeCollection(storeId, FirebaseCollections.Alerts)

    const id = this.firebaseInfo?.__id
    this.thisDocument = id ? `${this.thisCollection}/${id}` : this.thisCollection
  }

  isCurrentlyActive(now = Timestamp.now()): boolean {
    const nowMs = now.toMillis()
    const startOk = !this.startAt || this.startAt.toMillis() <= nowMs
    const endOk = !this.endAt || this.endAt.toMillis() >= nowMs
    return this.isActive && startOk && endOk
  }

  canBeSeenByRoles(userRoles: Roles[]): boolean {
    if (this.targetAll) return true
    return this.targetRoles.some((role) => userRoles.includes(role))
  }

  canBeSeenByAccount(accountId: string, userRoles: Roles[]): boolean {
    if (accountId && this.targetAccountIds.includes(accountId)) return true
    return this.canBeSeenByRoles(userRoles)
  }
}

export const StoreAlertConverter: FirestoreDataConverter<StoreAlert> = {
  toFirestore(alert: StoreAlert) {
    return {
      title: alert.title,
      body: alert.body,
      targetRoles: alert.targetRoles,
      targetAccountIds: alert.targetAccountIds,
      targetAll: alert.targetAll,
      startAt: alert.startAt,
      endAt: alert.endAt,
      isActive: alert.isActive,
      priority: alert.priority,
      source: alert.source,
      version: alert.version,
      metadata: alert.metadata,
      firebaseInfo: {
        ...alert.firebaseInfo
      }
    }
  },

  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions) {
    const data = snapshot.data(options)

    return new StoreAlert({
      ...data,
      firebaseInfo: createFirebaseInterface({
        ...data.firebaseInfo,
        __id: snapshot.id
      })
    })
  }
}
